import { supabase } from '../lib/supabase';

export const assetService = {
  // Get all assets
  async getAll(filters = {}) {
    let query = supabase?.from('assets')?.select('*')?.order('created_at', { ascending: false });

    if (filters?.category && filters?.category !== 'all') {
      query = query?.eq('category', filters?.category);
    }

    if (filters?.type) {
      query = query?.eq('file_type', filters?.type);
    }

    if (filters?.search) {
      query = query?.ilike('title', `%${filters?.search}%`);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    
    // Convert to camelCase and add full URLs
    return data?.map(asset => {
      const baseData = {
        id: asset?.id,
        title: asset?.title,
        description: asset?.description,
        assetType: asset?.asset_type,
        fileType: asset?.file_type,
        format: asset?.format,
        category: asset?.category,
        dimensions: asset?.dimensions,
        fileSize: asset?.file_size,
        orientation: asset?.orientation,
        campaign: asset?.campaign,
        uploadedBy: asset?.uploaded_by,
        createdAt: asset?.created_at,
        updatedAt: asset?.updated_at
      };

      // Add file-specific fields
      if (asset?.asset_type === 'file') {
        return {
          ...baseData,
          filePath: asset?.file_path,
          thumbnailPath: asset?.thumbnail_path,
          thumbnail: asset?.thumbnail_path ? supabase?.storage?.from('marketing-assets')?.getPublicUrl(asset?.thumbnail_path)?.data?.publicUrl : null,
          fullImage: supabase?.storage?.from('marketing-assets')?.getPublicUrl(asset?.file_path)?.data?.publicUrl
        };
      }

      // Add link-specific fields
      if (asset?.asset_type === 'link') {
        return {
          ...baseData,
          linkUrl: asset?.link_url,
          linkPreviewImage: asset?.link_preview_image,
          linkPreviewTitle: asset?.link_preview_title,
          linkPreviewDescription: asset?.link_preview_description,
          linkDomain: asset?.link_domain,
          thumbnail: asset?.link_preview_image,
          fullImage: asset?.link_preview_image
        };
      }

      return baseData;
    });
  },

  // Upload new asset
  async upload(file, metadata) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Generate unique file path
      const timestamp = Date.now();
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${metadata?.category}/${timestamp}-${file?.name?.replace(/[^a-zA-Z0-9.-]/g, '_')}`;

      // Upload file to storage
      const { data: uploadData, error: uploadError } = await supabase?.storage?.from('marketing-assets')?.upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Create metadata record
      const { data: assetData, error: dbError } = await supabase?.from('assets')?.insert({
          title: metadata?.title,
          description: metadata?.description,
          file_path: uploadData?.path,
          file_type: metadata?.fileType,
          format: fileExt?.toUpperCase(),
          category: metadata?.category,
          dimensions: metadata?.dimensions,
          file_size: this.formatFileSize(file?.size),
          orientation: metadata?.orientation,
          campaign: metadata?.campaign,
          thumbnail_path: uploadData?.path, // Use same path for now
          uploaded_by: user?.id
        })?.select()?.single();

      if (dbError) throw dbError;

      return {
        id: assetData?.id,
        title: assetData?.title,
        filePath: assetData?.file_path,
        thumbnail: supabase?.storage?.from('marketing-assets')?.getPublicUrl(assetData?.file_path)?.data?.publicUrl
      };
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  },

  // Upload link asset
  async uploadLink(data) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { linkUrl, linkPreview, title, description, category, campaign, orientation, dimensions } = data;

      // Create link asset record
      const { data: assetData, error: dbError } = await supabase?.from('assets')?.insert({
          asset_type: 'link',
          title: title,
          description: description,
          link_url: linkUrl,
          link_preview_image: linkPreview?.image || '',
          link_preview_title: linkPreview?.title || title,
          link_preview_description: linkPreview?.description || description,
          link_domain: linkPreview?.domain || '',
          file_type: 'link',
          format: 'LINK',
          category: category,
          dimensions: dimensions,
          orientation: orientation,
          campaign: campaign,
          uploaded_by: user?.id
        })?.select()?.single();

      if (dbError) throw dbError;

      return {
        id: assetData?.id,
        title: assetData?.title,
        linkUrl: assetData?.link_url,
        thumbnail: assetData?.link_preview_image
      };
    } catch (error) {
      console.error('Link upload error:', error);
      throw error;
    }
  },

  // Update asset metadata
  async update(assetId, updates) {
    const { data, error } = await supabase?.from('assets')?.update({
        title: updates?.title,
        description: updates?.description,
        category: updates?.category,
        campaign: updates?.campaign,
        orientation: updates?.orientation
      })?.eq('id', assetId)?.select('*');

    if (error) throw error;
    return data?.[0] || data;
  },

  // Replace asset file
  async replace(assetId, newFile) {
    try {
      // Check authentication - use getSession for more reliable auth check
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        throw new Error('Authentication failed. Please log out and log back in.');
      }
      
      if (!session || !session?.user) {
        throw new Error('Not authenticated. Please log in.');
      }

      // Get existing asset
      const { data: asset, error: fetchError } = await supabase?.from('assets')?.select('file_path, category')?.eq('id', assetId)?.single();

      if (fetchError) throw fetchError;

      // Delete old file
      const { error: deleteError } = await supabase?.storage?.from('marketing-assets')?.remove([asset?.file_path]);

      if (deleteError) throw deleteError;

      // Generate NEW unique file path (prevents cache issues)
      const timestamp = Date.now();
      const fileExt = newFile?.name?.split('.')?.pop();
      const fileName = `${asset?.category}/${timestamp}-${newFile?.name?.replace(/[^a-zA-Z0-9.-]/g, '_')}`;

      // Upload new file with new path
      const { data: uploadData, error: uploadError } = await supabase?.storage?.from('marketing-assets')?.upload(fileName, newFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Update database with new file path and file size
      const { error: updateError } = await supabase?.from('assets')?.update({
          file_path: uploadData?.path,
          thumbnail_path: uploadData?.path,
          file_size: this.formatFileSize(newFile?.size),
          format: fileExt?.toUpperCase()
        })?.eq('id', assetId);

      if (updateError) throw updateError;

      return { success: true };
    } catch (error) {
      console.error('Replace error:', error);
      throw error;
    }
  },

  // Delete asset
  async delete(assetId) {
    try {
      // Get asset info
      const { data: asset, error: fetchError } = await supabase?.from('assets')?.select('file_path, asset_type')?.eq('id', assetId)?.single();

      if (fetchError) throw fetchError;

      // Delete from storage only if it's a file asset
      if (asset?.asset_type === 'file' && asset?.file_path) {
        const { error: storageError } = await supabase?.storage?.from('marketing-assets')?.remove([asset?.file_path]);
        if (storageError) throw storageError;
      }

      // Delete from database
      const { error: dbError } = await supabase?.from('assets')?.delete()?.eq('id', assetId);

      if (dbError) throw dbError;

      return { success: true };
    } catch (error) {
      console.error('Delete error:', error);
      throw error;
    }
  },

  // Helper function to format file size
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  }
};